/* eslint-disable @typescript-eslint/naming-convention */
import React, { Component } from 'react'
import Draggable, { DraggableEvent } from 'react-draggable'
import { DefaultArrowTooltip } from 'tooltips'
import { Typography, IconButton, Icon, Skeleton } from 'foundations-library/components'
import { withStyles } from '@material-ui/styles'
import styles from './styles'

function formatSecondsToMinutes (seconds) {
    if (isNaN(seconds)) return '0:00'
    const minutes = Math.floor(seconds / 60)
    seconds = (seconds - (minutes * 60)).toFixed(0)
    if (seconds < 10) seconds = '0' + seconds
    return (`${minutes}:${seconds}`)
}

interface Props {
    classes: Record<string, string>
    url: string
    preload?: '' | 'none' | 'metadata' | 'auto'
    duration?: number
    onError?: () => void
    onPlay?: () => void
    onReadyToPlay?: () => void
    disabled?: boolean
    playFromSecond?: number
    downloadable?: boolean
}

class AudioPlayer extends Component<Props> {
    seekRef: React.RefObject<HTMLDivElement>
    progressRef: React.RefObject<HTMLDivElement>
    audio: HTMLAudioElement
    state: Record<string, any>
    _mounted: boolean
    controlSeek: (event: React.MouseEvent<HTMLDivElement>) => void
    toggleAudio: () => void
    initializeAudioState: (event?: Event) => void
    updateCurrentTime: (event?: Event) => void
    adjustVolume: (event: React.MouseEvent<HTMLDivElement>) => void

    constructor (props: Props) {
        super(props)

        this.seekRef = React.createRef()
        this.progressRef = React.createRef()
        this.audio = new Audio(props.url)
        this.audio.preload = props.preload || 'auto'
        this._mounted = false
        this.state = {
            progress: 0,
            selected: false,
            play: false,
            duration: props.duration || 0,
            volume: { value: 50 },
            error: false,
            playerControlPosition: null,
            dragState: 'inactive',
            controlButtonHovered: false,
            readyToPlay: false
        }

        this.audio.onerror = e => {
            if (!this._mounted) return
            if (this.props.onError) this.props.onError()
            this.setState({ error: true })
        }

        this.controlSeek = (event: React.MouseEvent<HTMLDivElement>) => {
            if (Array.from((event.target as HTMLElement).classList).includes('player-control')) return
            const seekbarRect = this.seekRef.current!.getBoundingClientRect()
            const left = event.pageX - seekbarRect.left
            const percentage = left / seekbarRect.width
            const seekTime = this.getDuration() * percentage
            this.audio.currentTime = seekTime
            this.setState({ progress: percentage * 100, playerControlPosition: { x: left, y: 0 } })
        }

        this.toggleAudio = () => {
            if (this.state.play === true) {
                this.setState({ play: false })
                this.audio.pause()
            } else {
                this.setState({ loading: true })
                this.audio.play().then(() => {
                    this.setState({ play: true, loading: false }, () => {
                        if (this.props.onPlay) this.props.onPlay()
                    })
                }).catch(e => {
                    if (this.props.onError) this.props.onError()
                    this.setState({ error: true, loading: false })
                    window.alert('unable to play audio')
                })
            }
        }

        this.initializeAudioState = event => {
            const d = this.getDuration()
            this.setState({
                error: false,
                duration: Number.isFinite(d) ? d.toFixed(0) : 0
            })
            this.onReadyToPlay()
        }

        this.updateCurrentTime = event => {
            this.setState({ progress: this.audio.currentTime / this.getDuration() * 100 })
            if (this.state.dragState === 'inactive') {
                const playerControlPosition = { x: this.progressRef.current!.offsetWidth, y: 0 }
                this.setState({ playerControlPosition })
            }
            if (this.state.progress >= 100) {
                this.setState({ play: false })
            }
        }

        this.adjustVolume = (event: React.MouseEvent<HTMLDivElement>) => {
            this.setState({ volume: { value: (event.target as HTMLInputElement).value } })
            this.audio.volume = this.state.volume.value / 100
        }
    }

    onReadyToPlay = () => {
        if (this.props.onReadyToPlay) this.props.onReadyToPlay()
        this.setState({ readyToPlay: true })
    }

    getDuration = () => (this.props.duration || this.props.duration === 0) ? this.props.duration : this.audio.duration

    componentDidMount = () => {
        this._mounted = true
        this.resetEventListeners()
    }

    resetEventListeners = () => {
        this.audio.removeEventListener('loadedmetadata', this.initializeAudioState)
        this.audio.removeEventListener('timeupdate', this.updateCurrentTime)
        this.audio.addEventListener('loadedmetadata', this.initializeAudioState)
        this.audio.addEventListener('timeupdate', this.updateCurrentTime)
    }

    componentWillUnmount = () => {
        this.audio.removeEventListener('loadedmetadata', this.initializeAudioState)
        this.audio.removeEventListener('timeupdate', this.updateCurrentTime)
        this.audio.pause()
        this._mounted = false
    }

    // Pause player when disable flag is triggered
    componentDidUpdate (prevProps) {
        if (!prevProps.duration && this.props.duration) this.onReadyToPlay()
        if (prevProps.url !== this.props.url) {
            this.audio.pause()
        }
        if (this.props.disabled && this.state.play) {
            this.setState({ play: false })
            this.audio.pause()
        }
        if ((!prevProps.url && this.props.url) || prevProps.url !== this.props.url) {
            this.audio = new Audio(this.props.url)
            this.resetEventListeners()
        }
        if (this.props.playFromSecond && prevProps.playFromSecond !== this.props.playFromSecond) {
            let playFromSecond = this.props.playFromSecond
            const duration = this.getDuration()
            if (playFromSecond > duration) playFromSecond = duration
            const progress = 100 * playFromSecond / duration
            this.audio.currentTime = playFromSecond
            this.setState({ progress })
            if (!this.state.play) this.toggleAudio()
        }
    }

    onDragStart = () => this.setState({ dragState: 'active' })

    onDragStop = (e: DraggableEvent, info: { x: number }) => {
        this.setState({ dragState: 'inactive' })
        const parentWidth = this.seekRef.current!.offsetWidth
        const positionX = info.x
        const progressWidth = 100 * positionX / parentWidth
        this.setState({ progress: progressWidth, dragTime: null })

        const seekTime = this.getDuration() * progressWidth / 100
        this.audio.currentTime = seekTime
    }

    controlDragged = (e: DraggableEvent, info: { x: number }) => {
        this.setState({ playerControlPosition: null })

        const parentWidth = this.seekRef.current!.offsetWidth
        const positionX = info.x
        const progressWidth = 100 * positionX / parentWidth

        if (!this.state.play) {
            this.setState({ progress: progressWidth })
        }

        const dragTime = formatSecondsToMinutes(this.getDuration() * progressWidth / 100)
        this.setState({ dragTime, tempProgress: progressWidth })
    }

    setControlButtonHovered = controlButtonHovered => this.setState({ controlButtonHovered })

    renderCurrentTime = (currentTime: string) => {
        if (this.state.readyToPlay) {
            return <Typography variant='body3' color='neutral-500'>{currentTime}</Typography>
        }
        return <Skeleton variant='text' width={25} />
    }

    renderTotalTime = (totalTime: string) => {
        if (this.state.readyToPlay) {
            return <Typography variant='body3' color='neutral-500'>{totalTime}</Typography>
        }
        return <Skeleton variant='text' width={25} />
    }

    renderSeekBar = (currentTime: string) => {
        if (this.state.readyToPlay) {
            const { classes } = this.props
            const styles = {
                seek: {
                    width: `${this.state.progress}%`
                }
            }
            return (
                <div
                    className = {`${classes.audioControlSeekbar} ${this.state.error ? classes.seekBarErrorBorder : classes.seekBarBorder}`}
                    onClick = {this.controlSeek}
                    ref = {this.seekRef}
                >
                    <span className='progress-bar' style={styles.seek} ref={this.progressRef}>
                        <Draggable
                            axis = 'x'
                            grid = {[1, 0]}
                            bounds = {{ left: 0, right: this.seekRef.current ? this.seekRef.current.offsetWidth : 0 }}
                            positionOffset = {{ x: -17.5, y: 0 }}
                            onStart = {this.onDragStart}
                            onStop = {this.onDragStop}
                            onDrag = {this.controlDragged}
                            position = {this.state.playerControlPosition}
                        >
                            <DefaultArrowTooltip
                                title = {this.state.dragTime || currentTime}
                                placement = 'top'
                            >
                                <div className={`player-control ${this.state.dragState === 'active' ? 'force-show' : ''}`}>
                                    <div className='circle-icon'></div>
                                </div>
                            </DefaultArrowTooltip>
                        </Draggable>
                    </span>
                    {this.state.dragState === 'active' && this.state.play
                        ? <span style={{ width: `${this.state.tempProgress}%` }} className='temp-progress-bar'></span>
                        : null
                    }
                </div>
            )
        }
        return <Skeleton variant='text' height={4} style={{ flexGrow: 1 }} />
    }

    renderControlButton = () => {
        const { loading, readyToPlay, play } = this.state
        const { classes } = this.props

        if (loading || !readyToPlay) {
            return <Skeleton variant='circle' width={44} height={44} />
        }

        return (
            <div
                onMouseEnter={() => this.setControlButtonHovered(true)}
                onMouseLeave={() => this.setControlButtonHovered(false)}
            >
                <IconButton
                    size='large'
                    data-testid='audio-player-play-btn'
                    className={classes.controlButton}
                    variant='contained'
                    name={play ? 'pause' : 'play'}
                    onClick={this.toggleAudio}
                />
            </div>
        )
    }

    renderDownloadButton = () => {
        if (this.props.downloadable) {
            return <a href={this.props.url} download='call-recording' target='_blank' rel='noreferrer'>
                <Icon size='large' data-testid='download-recording-icon-btn' color='neutral' name='download' />
            </a>
        }
    }

    render () {
        const { classes } = this.props
        const currentTime = formatSecondsToMinutes(this.audio.currentTime <= this.getDuration() ? this.audio.currentTime : this.getDuration())
        const totalTime = formatSecondsToMinutes(this.state.duration)

        return (
            <div className={classes.audioControlContainer} data-testid='audio-player-container'>
                <div className={classes.progressAndTimingWrapper}>
                    {this.renderCurrentTime(currentTime)}
                    {this.renderSeekBar(currentTime)}
                    {this.renderTotalTime(totalTime)}
                </div>
                {this.renderControlButton()}
                {this.renderDownloadButton()}
            </div>
        )
    }
}

export default withStyles(styles)(AudioPlayer)
